import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Badge from "@/components/common/Badge";
import ConfirmDialog, { DeleteIcon } from "@/components/common/ConfirmDialog";
import ClienteDetalleModal from "./ClienteDetalleModal";
import { formatPhone, formatRut } from "@/utils/formatters";
import { normalizeDataObject } from "@/utils/normalizationUtils";
import moment from 'moment';
import {
  FaUser,
  FaPhone,
  FaEnvelope,
  FaIdCard,
  FaPen,
  FaTrash,
  FaCar,
  FaCalendarAlt,
  FaPlus,
  FaEye,
} from "react-icons/fa";

/**
 * Componente para mostrar un cliente individual
 * Similar al diseño de tabla pero en formato card
 * Incluye: Nombre, RUT, Email, Teléfono, Cantidad de Vehículos, Último Servicio, Estado
 *
 * @param {Object} props - Propiedades del componente
 * @param {Object} props.cliente - Datos del cliente
 * @param {function} props.onEdit - Función para editar el cliente
 * @param {function} props.onDelete - Función para eliminar el cliente
 * @returns {JSX.Element} - Componente de ítem de cliente
 */

const ClienteItem = ({ cliente, onEdit, onDelete }) => {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDetalleModalOpen, setIsDetalleModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();
  
  // Normalizar datos del cliente para consistencia
  const normalizedCliente = normalizeDataObject(cliente, 'cliente');

  // Detectar si estamos en móvil
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // Pantallas menores a 768px se consideran móviles
    };

    // Comprobar inicialmente
    checkMobile();

    // Actualizar cuando cambie el tamaño de la ventana
    window.addEventListener("resize", checkMobile);

    // Limpiar al desmontar
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Handler para ir a la página de vehículos
  const handleVehiculosClick = () => {
    navigate(`/vehiculos/${normalizedCliente.id || normalizedCliente.Id}`);
  };

  // Handler para abrir el modal de detalles
  const handleCardClick = (e) => {
    // Evitar que se abra el modal si se hace clic en los botones de acción
    if (e.target.closest('button') || e.target.closest('[data-action-button]')) {
      console.log('🛑 Clic en botón, no abrir modal');
      return;
    }
    console.log('👆 Clic en card, abriendo modal para cliente:', normalizedCliente.id);
    e.preventDefault();
    e.stopPropagation();
    setIsDetalleModalOpen(true);
  };

  // Función para obtener el color de borde según la cantidad de vehículos
  const getBorderColor = () => {
    const cantidadVehiculos = normalizedCliente.vehiculos_count || 0;
    
    if (cantidadVehiculos === 0) return 'border-gray-500'; // Sin vehículos
    if (cantidadVehiculos <= 2) return 'border-blue-500'; // Pocos vehículos
    if (cantidadVehiculos <= 5) return 'border-green-500'; // Cantidad normal
    return 'border-yellow-500'; // Muchos vehículos
  };

  // Obtener datos adicionales del cliente - ahora desde el backend
  const cantidadVehiculos = normalizedCliente.vehiculos_count || 0;
  const ultimoServicio = moment(normalizedCliente.ultimo_servicio).format('DD/MM/YYYY') || '-';
  const rut = normalizedCliente.rut || normalizedCliente.RUT || '';
  const rutFormateado = rut ? formatRut(rut) : '';
  const email = normalizedCliente.email || normalizedCliente.Correo || '';
  const telefono = normalizedCliente.telefono || normalizedCliente.Telefono || '';
  const nombre = normalizedCliente.nombre || normalizedCliente.Nombre || '';
  const apellido = normalizedCliente.apellido || normalizedCliente.Apellido || '';
  const nombreCompleto = `${nombre}${apellido ? ' ' + apellido : ''}`;
  const isActivo = normalizedCliente.activo !== false;
  const vehiculosText = `${cantidadVehiculos} vehículo${cantidadVehiculos !== 1 ? 's' : ''}`;

  return (
    <>
      <div 
        onClick={handleCardClick}
        className={`card mb-3 border-l-4 ${getBorderColor()} transition-all duration-300 cursor-pointer hover:bg-gray-700 shadow-md`}
      >
        <div className="p-0">
          {isMobile ? (
            <div>
              {/* Versión móvil */}
              <div className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold text-sm mr-3">
                      {nombre.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white">{nombreCompleto}</h3>
                      <div className="flex items-center text-xs text-gray-400">
                        <FaIdCard className="mr-1 w-3" />
                        <span>{rutFormateado}</span>
                      </div>
                    </div>
                  </div>
                  <Badge
                    variant={isActivo ? "success" : "secondary"}
                    size="sm"
                  >
                    {isActivo ? "Activo" : "Inactivo"}
                  </Badge>
                </div>
                
                <div className="space-y-2 text-sm">
                  {email && (
                    <div className="flex items-center text-gray-300">
                      <FaEnvelope className="mr-2 text-gray-500 w-4" />
                      <span>{email}</span>
                    </div>
                  )}
                  {telefono && (
                    <div className="flex items-center text-gray-300">
                      <FaPhone className="mr-2 text-gray-500 w-4" />
                      <span>{formatPhone(telefono)}</span>
                    </div>
                  )}
                  <div className="flex items-center text-gray-300">
                    <FaCar className="mr-2 text-gray-500 w-4" />
                    <span>{vehiculosText}</span>
                  </div>
                  <div className="flex items-center text-gray-300">
                    <FaCalendarAlt className="mr-2 text-gray-500 w-4" />
                    <span>
                      Último servicio: {ultimoServicio}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex justify-between border-t border-gray-700 bg-gray-800" data-action-button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsDetalleModalOpen(true);
                  }}
                  className="flex-1 py-3 flex flex-col items-center justify-center text-blue-500"
                  title="Ver Detalles"
                  data-action-button
                >
                  <FaEye className="text-lg" />
                  <span className="text-xs mt-1">Detalles</span>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(cliente);
                  }}
                  className="flex-1 py-3 flex flex-col items-center justify-center text-yellow-500"
                  title="Editar"
                  data-action-button
                >
                  <FaPen className="text-lg" />
                  <span className="text-xs mt-1">Editar</span>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsDeleteDialogOpen(true);
                  }}
                  className="flex-1 py-3 flex flex-col items-center justify-center text-red-500"
                  title="Eliminar"
                  data-action-button
                >
                  <FaTrash className="text-lg" />
                  <span className="text-xs mt-1">Eliminar</span>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleVehiculosClick();
                  }}
                  className="flex-1 py-3 flex flex-col items-center justify-center text-green-500"
                  title="Vehículos"
                  data-action-button
                >
                  <FaCar className="text-lg" />
                  <span className="text-xs mt-1">Vehículos</span>
                </button>
              </div>
            </div>
          ) : (
            <div className="p-4">
              {/* Versión escritorio - layout similar a tabla */}
              <div className="grid grid-cols-12 gap-4 items-center">
                {/* Nombre con avatar circular */}
                <div className="col-span-3 flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold text-sm mr-3">
                      {nombre.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h3 className="font-semibold text-white text-sm">{nombreCompleto}</h3>
                      <div className="flex items-center text-xs text-gray-400">
                        <FaIdCard className="mr-1 w-3" />
                        <span>{rutFormateado}</span>
                      </div>
                    </div>
                  </div>
                  <div className="ml-2">
                    <Badge
                      variant={isActivo ? "success" : "secondary"}
                      size="sm"
                    >
                      {isActivo ? "Activo" : "Inactivo"}
                    </Badge>
                  </div>
                </div>
                
                {/* Email */}
                <div className="col-span-2 text-sm text-gray-300 flex items-center">
                  <FaEnvelope className="mr-2 text-gray-500 w-4" />
                  <span className="truncate">{email || '-'}</span>
                </div>
                
                {/* Teléfono */}
                <div className="col-span-2 text-sm text-gray-300 flex items-center">
                  <FaPhone className="mr-2 text-gray-500 w-4" />
                  <span>{telefono ? formatPhone(telefono) : '-'}</span>
                </div>
                
                {/* Vehículos */}
                <div className="col-span-1 text-sm text-gray-300 flex items-center">
                  <FaCar className="mr-2 text-gray-500 w-4" />
                  <span>{vehiculosText}</span>
                </div>
                
                {/* Último Servicio */}
                <div className="col-span-3 text-sm text-gray-300 flex items-center">
                  <FaCalendarAlt className="mr-2 text-gray-500 w-4" />
                  <span className="truncate">
                    {ultimoServicio}
                  </span>
                </div>
                
                {/* Botones de acción */}
                <div className="col-span-1 flex gap-2 justify-end">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsDetalleModalOpen(true);
                    }}
                    className="text-white bg-blue-600 hover:bg-blue-700 px-3 py-2 rounded flex items-center justify-center min-w-[2.4rem] h-7"
                    title="Ver Detalles"
                    data-action-button
                  >
                    <FaEye />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleVehiculosClick();
                    }}
                    className="text-white bg-green-600 hover:bg-green-700 px-3 py-2 rounded flex items-center justify-center min-w-[2.4rem] h-7"
                    title="Vehículos"
                    data-action-button
                  >
                    <FaCar/>
                  </button>
                  <button
                    onClick={() => onEdit(cliente)}
                    className="text-white bg-yellow-600 hover:bg-yellow-700 px-3 py-2 rounded flex items-center justify-center min-w-[2.4rem] h-7"
                    title="Editar"
                  >
                    <FaPen />
                  </button>
                  <button
                    onClick={() => setIsDeleteDialogOpen(true)}
                    className="text-white bg-red-600 hover:bg-red-700 px-3 py-2 rounded flex items-center justify-center min-w-[2.4rem] h-7"
                    title="Eliminar"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={() => {
          onDelete(cliente.id || cliente.Id);
          setIsDeleteDialogOpen(false);
        }}
        title="Eliminar cliente"
        message={`¿Estás seguro de que deseas eliminar al cliente "${normalizedCliente.Nombre || normalizedCliente.nombre}"? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        confirmVariant="danger"
        icon={<DeleteIcon />}
      />

      {/* Modal de Detalles del Cliente */}
      <ClienteDetalleModal
        isOpen={isDetalleModalOpen}
        onClose={() => setIsDetalleModalOpen(false)}
        cliente={cliente}
      />
    </>
  );
};

export default ClienteItem;
